import { create } from 'zustand';
import type { User, AuthTokens } from 'types/user.types';
import { authApi, setAccessToken, registerAuthHandlers } from 'lib/axios';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isBootstrapping: boolean;
  failedAttempts: number;
  lockedUntil: number | null;

  setAuth: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  incrementFailed: () => void;
  lockFor: (seconds: number) => void;
  resetFailed: () => void;
  bootstrap: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isBootstrapping: true,
  failedAttempts: 0,
  lockedUntil: null,

  setAuth: (user, tokens) => {
    // L'access token ne vit qu'en mémoire ; le refresh est un cookie httpOnly.
    setAccessToken(tokens.accessToken);
    set({ user, token: tokens.accessToken, isAuthenticated: true, failedAttempts: 0, lockedUntil: null });
  },

  logout: () => {
    authApi.logout().catch(() => {});
    setAccessToken(null);
    set({ user: null, token: null, isAuthenticated: false });
  },

  incrementFailed: () => {
    const attempts = get().failedAttempts + 1;
    const lockedUntil = attempts >= 3 ? Date.now() + 30_000 : null;
    set({ failedAttempts: attempts, lockedUntil });
  },

  // Verrou imposé par le serveur (429) : la durée fait autorité.
  lockFor: (seconds: number) => {
    set({ lockedUntil: Date.now() + Math.max(0, seconds) * 1000 });
  },

  resetFailed: () => set({ failedAttempts: 0, lockedUntil: null }),

  // Restaure la session au démarrage via le cookie httpOnly de refresh.
  // C'est ce qui fait survivre la session au redémarrage de la PWA mobile.
  bootstrap: async () => {
    try {
      const data = await authApi.refresh();
      setAccessToken(data.accessToken);
      set({ user: data.user, token: data.accessToken, isAuthenticated: true });
    } catch {
      setAccessToken(null);
      set({ user: null, token: null, isAuthenticated: false });
    } finally {
      set({ isBootstrapping: false });
    }
  },
}));

// Permet à l'intercepteur axios de mettre à jour le store sans import circulaire.
registerAuthHandlers({
  onRefreshed: (user, accessToken) => {
    useAuthStore.setState({ user: user as unknown as User, token: accessToken, isAuthenticated: true });
  },
  onAuthLost: () => {
    useAuthStore.setState({ user: null, token: null, isAuthenticated: false });
  },
});
